import * as React from "react";

import { Svg } from "@impero-com/ui";

import { assertNever } from "../../utils/obj";
import { nullToEmpty } from "../../utils/string";

import * as theme from "./theme.css";

interface BaseToolSpec {
    label: string;
    onCommand: () => void;
    isEnabled?: () => boolean;
}

interface TextToolSpec extends BaseToolSpec {
    type: "Text";
}

interface IconSpec {
    icon: string;
}

interface ImageSpec {
    image: string;
    imageClassName?: string;
}

interface IconToolSpec extends BaseToolSpec, IconSpec {
    type: "Icon";
}

interface ImageToolSpec extends BaseToolSpec, ImageSpec {
    type: "Image";
}

interface DynamicToolSpec {
    type: "Dynamic";
    getTool: () => React.ReactNode;
}

interface ImageDividerSpec {
    type: "ImageDivider";
    image: string;
}

interface SpacerToolSpec {
    type: "Spacer";
    flex?: "auto";
}

interface BaseToggleButton<ID> {
    id: ID;
}

interface IconToggleButton<ID> extends BaseToggleButton<ID>, IconSpec {
    type: "IconToggle";
}

interface ImageToggleButton<ID> extends BaseToggleButton<ID>, ImageSpec {
    type: "ImageToggle";
}

type ToggleButton<ID> = IconToggleButton<ID> | ImageToggleButton<ID>;

interface ToggleToolSpec<ID> {
    type: "Toggle";
    toggleButtons: Array<ToggleButton<ID>>;
    activeButtonId: ID;
    onCommand: (toggleId: ID) => void;
    isEnabled?: () => boolean;
}

type ToolSpec =
    | TextToolSpec
    | IconToolSpec
    | ImageToolSpec
    | DynamicToolSpec
    | ToggleToolSpec<any>
    | ImageDividerSpec
    | SpacerToolSpec;

class Toolbar extends React.Component<ToolbarProps> {
    render() {
        const { children, toolbarClassName, floatingToolbar } = this.props;
        const realToolbarClassName =
            toolbarClassName ||
            `${theme.reactableToolbar} ${theme.reactableToolbarNew}`;
        const toolbarClassNames: Array<string> = [realToolbarClassName];
        if (floatingToolbar) {
            toolbarClassNames.push(theme.reactableFloatingToolbar);
        }
        return (
            <div className={theme.reactableToolbarContainer}>
                <div className={toolbarClassNames.join(" ")}>
                    {this.buildTools()}
                </div>
                <div className={theme.content}>{children}</div>
            </div>
        );
    }
    private buildTools() {
        const { tools } = this.props;
        if (!tools) {
            return null;
        }

        let needSpacer = true;
        const propTools = tools.map((tool, index) => {
            switch (tool.type) {
                case "Dynamic":
                    return (
                        <span className={theme.dynamicTool} key={index}>
                            {tool.getTool()}
                        </span>
                    );
                case "Spacer":
                    const { flex } = tool;
                    if (flex) {
                        if (flex === "auto") {
                            needSpacer = false;
                        }
                        return (
                            <span
                                key={index}
                                className={theme.spacer}
                                style={{ flex }}
                            />
                        );
                    } else {
                        return <span key={index} className={theme.spacer} />;
                    }
                case "ImageDivider":
                    return (
                        <Svg
                            key={index}
                            className={theme.imageDivider}
                            src={tool.image}
                        />
                    );
                case "Toggle": {
                    let enabled = true;
                    if (tool.isEnabled) {
                        enabled = tool.isEnabled();
                    }
                    return (
                        <span
                            key={index}
                            className={theme.toggleButtonsContainer}
                        >
                            {tool.toggleButtons.map((toggleButton) => {
                                const isActive =
                                    enabled &&
                                    tool.activeButtonId === toggleButton.id;
                                return (
                                    <span
                                        key={toggleButton.id}
                                        className={
                                            isActive
                                                ? theme.toggleButtonActive
                                                : theme.toggleButtonInactive
                                        }
                                        onClick={
                                            !isActive
                                                ? () =>
                                                      tool.onCommand(
                                                          toggleButton.id
                                                      )
                                                : undefined
                                        }
                                    >
                                        {this.buildToggleButton(toggleButton)}
                                    </span>
                                );
                            })}
                        </span>
                    );
                }
                case "Icon":
                case "Image": {
                    let enabled = true;
                    if (tool.isEnabled) {
                        enabled = tool.isEnabled();
                    }
                    let icon: React.ReactNode;
                    switch (tool.type) {
                        case "Icon":
                            icon = <span className={tool.icon} />;
                            break;
                        case "Image":
                            const className =
                                tool.imageClassName || theme.imageToolImage;
                            icon = (
                                <Svg className={className} src={tool.image} />
                            );
                            break;
                        default:
                            return assertNever(tool);
                    }
                    const containerClassName = enabled
                        ? theme.buttonContainerEnabled
                        : theme.buttonContainerDisabled;
                    const buttonChildren: Array<React.ReactNode> = [];
                    if (icon) {
                        buttonChildren.push(
                            <span key={buttonChildren.length}>{icon}</span>
                        );
                    }
                    if (tool.label) {
                        if (buttonChildren.length > 0) {
                            buttonChildren.push(
                                <span
                                    key={buttonChildren.length}
                                    className={theme.toolLabelSpace}
                                >
                                    {" "}
                                </span>
                            );
                        }
                        buttonChildren.push(
                            <span
                                key={buttonChildren.length}
                                className={
                                    enabled
                                        ? theme.toolLabel
                                        : theme.disabledToolLabel
                                }
                            >
                                {tool.label}
                            </span>
                        );
                    }
                    return (
                        <span key={index} className={containerClassName}>
                            <button
                                disabled={!enabled}
                                onClick={() => {
                                    if (tool.onCommand) {
                                        tool.onCommand();
                                    }
                                }}
                            >
                                {buttonChildren}
                            </button>
                        </span>
                    );
                }
                case "Text": {
                    const enabled = tool.isEnabled ? tool.isEnabled() : true;
                    return (
                        <span
                            key={index}
                            className={
                                enabled
                                    ? theme.textTool
                                    : theme.disabledTextTool
                            }
                            onClick={
                                enabled
                                    ? () => {
                                          if (tool.onCommand) {
                                              tool.onCommand();
                                          }
                                      }
                                    : undefined
                            }
                        >
                            {tool.label}
                        </span>
                    );
                }
                default:
                    return assertNever(tool);
            }
        });
        return propTools.concat(
            needSpacer
                ? [
                      <span
                          key="rightSpacer"
                          className={theme.spacer}
                          style={{ flex: "auto" }}
                      />,
                  ]
                : []
        );
    }
    private buildToggleButton<ID>(toggleButton: ToggleButton<ID>) {
        switch (toggleButton.type) {
            case "IconToggle":
                return (
                    <span
                        className={`${toggleButton.icon} ${theme.toggleButtonContent}`}
                    />
                );
            case "ImageToggle":
                return (
                    <Svg
                        src={toggleButton.image}
                        className={`${nullToEmpty(
                            toggleButton.imageClassName
                        )} ${theme.toggleButtonContent}`}
                    />
                );
            default:
                return assertNever(toggleButton);
        }
    }
}

interface ToolbarProps {
    tools: Array<ToolSpec>;
    floatingToolbar?: boolean;
    toolbarClassName?: string;
    children?: React.ReactNode;
}

export type {
    ToolbarProps,
    TextToolSpec,
    IconToolSpec,
    ImageToolSpec,
    DynamicToolSpec,
    ToggleToolSpec,
    SpacerToolSpec,
    ImageDividerSpec,
    ToolSpec,
};
export { Toolbar, theme as ToolBarTheme };
